import { CodedProperty, ensure, NamedProperty } from "sprint-common-web";

import {
    AncillaryContentSubtype,
    ensureAncillaryContentSubtype
} from "../../../../../../common/types/AncillaryContentType";
import { BuzzContentReference } from "../../../../../../common/types/BuzzContentReference";
import { ContentSubtype } from "../../../../../../common/types/ContentSubtype";
import { ensureTvSeriesContentSubtype } from "../../../../../../common/types/TvSeriesContentSubtype";
import { useAssociateHistory } from "../../../../../quality-control/useAssociateHistory";
import { useSetLookupContent } from "../hooks/useSetLookupContent";

export interface AssociateHistoryItem {
    id: string;
    type: ContentSubtype;
    title: string;
    versions: { id: string; name: string }[];
}

export const useAssociateLookupHistory = () => {
    const { setMovieContent, setTvContent, setAncillaryContent } = useSetLookupContent();

    const associateHistory = useAssociateHistory();

    const associateSubmissions = associateHistory.read();

    const setContentFromLookupHistory = async (item: AssociateHistoryItem) => {
        switch (item.type) {
            case ContentSubtype.Movie:
            case ContentSubtype.TvSingle:
            case ContentSubtype.FeatureDocumentary:
            case ContentSubtype.ShortFilm: {
                setMovieContent(
                    item.id,
                    item.versions.map((v) => v.id)
                );

                break;
            }

            case ContentSubtype.TV: {
                const tvSeries = associateSubmissions.find((sub) => sub.id === item.id && hasSameVersions(item, sub));

                ensure(tvSeries);
                ensureTvSeriesContentSubtype(tvSeries.type);

                setTvContent({
                    tvSeriesId: tvSeries.id,
                    seasonId: tvSeries.seasonId,
                    episodeId: tvSeries.episodeId,
                    versionIds: tvSeries.versions.map((v) => v.id)
                });

                break;
            }

            case ContentSubtype.Promo:
            case ContentSubtype.Advert:
            case ContentSubtype.Safety:
            case ContentSubtype.ClientSpecific:
            case ContentSubtype.IndentWarning: {
                const ancillary = associateSubmissions.find((sub) => sub.id === item.id);

                ensure(ancillary);
                ensureAncillaryContentSubtype(ancillary.type);

                setAncillaryContent(ancillary.id, ancillary.airline);

                break;
            }
        }
    };

    return { setContentFromLookupHistory };
};

type VersionedItem = {
    versions: {
        id: string;
        name: string;
    }[];
};

const hasSameVersions = <TItem extends VersionedItem>(item: TItem, referenceItem: TItem) => {
    const versionIds = item.versions.map((v) => v.id);
    const versions = new Set(versionIds);
    const referenceVersions = new Set(referenceItem.versions.map((v) => v.id));

    return versions.size === referenceVersions.size && versionIds.every((v) => referenceVersions.has(v));
};

export interface OnPremAncillaryItem {
    id: string;
    title: string;
    subtype: AncillaryContentSubtype;
    buzz?: BuzzContentReference;
    version: OnPremQcAncillaryVersionItem;
    isMerged: boolean;
}

export interface OnPremQcAncillaryVersionItem {
    id: string;
    type: NamedProperty;
    airlines: CodedProperty[];
    distributor: NamedProperty;
    buzzId?: string;
}
